import { useCallback, useEffect, useRef, useState } from 'react';
import { NotebookPenIcon, PlusIcon, UploadIcon, MessageSquareIcon, CheckIcon } from 'lucide-react';
import { useAppLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import memberNotesCopy from '@/i18n/ns/meeting-member-notes';
import { useWorkspace } from './context';
import { emptyTurnContext, type CompanyMeeting, type MeetingMemberNote } from './types';
import { subscribeMeetingUpdates } from './meeting-events';
import { meetingsApi } from './meetings-api';

export type MeetingNoteDraft = { content: string; clientNoteId: string; fileName?: string };
export const emptyMeetingNoteDraft = (): MeetingNoteDraft => ({
  content: '',
  clientNoteId: crypto.randomUUID(),
});
const MAX_CHARACTERS = 12_000;

function Note({
  note,
  onRemove,
}: {
  note: MeetingMemberNote;
  onRemove: (id: string) => Promise<void>;
}) {
  const copy = useNamespaceCopy(memberNotesCopy);
  const locale = useAppLocale();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const name = note.author ?? copy.unknownAuthor;
  return (
    <article className="ws-member-note" data-note-id={note.id}>
      <div className="ws-member-note-byline">
        <span className="ws-member-note-avatar" aria-hidden="true">
          {name.slice(0, 1)}
        </span>
        <strong>{name}</strong>
        <time dateTime={note.createdAt}>
          {new Intl.DateTimeFormat(locale, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }).format(new Date(note.createdAt))}
        </time>
        {note.source === 'chat' && <span className="ws-member-note-source">{copy.fromChat}</span>}
        {note.fileName && <span className="ws-member-note-source">{note.fileName}</span>}
      </div>
      <p className="ws-member-note-text">{note.content}</p>
      {note.canDelete && (
        <div className="ws-member-note-remove">
          {confirming ? (
            <>
              <span>{copy.removeQuestion}</span>
              <button
                className="ws-text-button"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void onRemove(note.id)
                    .then(() => setConfirming(false))
                    .finally(() => setBusy(false));
                }}
              >
                {copy.remove}
              </button>
              <button
                className="ws-text-button"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                {copy.cancel}
              </button>
            </>
          ) : (
            <button className="ws-text-button" onClick={() => setConfirming(true)}>
              {copy.remove}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export function MeetingMemberNotes({
  meeting,
  draft,
  onDraft,
}: {
  meeting: CompanyMeeting;
  draft: MeetingNoteDraft;
  onDraft: (draft: MeetingNoteDraft) => void;
}) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(memberNotesCopy);
  const [notes, setNotes] = useState<MeetingMemberNote[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [open, setOpen] = useState(Boolean(draft.content));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const focusEditor = useRef(false);
  const active = useRef(true);
  const canAdd = meeting.canAddNotes ?? meeting.canEdit !== false;
  const load = useCallback(
    async (offset = 0) => {
      setLoading(true);
      try {
        const page = await meetingsApi.listMemberNotes(meeting.id, offset);
        if (!active.current) return;
        setNotes((current) =>
          offset
            ? [
                ...current,
                ...page.notes.filter((note) => !current.some((item) => item.id === note.id)),
              ]
            : page.notes,
        );
        setNextOffset(page.nextOffset);
        setError('');
      } catch {
        if (active.current) setError(copy.loadFailed);
      } finally {
        if (active.current) setLoading(false);
      }
    },
    [meeting.id, copy.loadFailed],
  );
  useEffect(() => {
    active.current = true;
    void load();
    return () => {
      active.current = false;
    };
  }, [load]);
  useEffect(
    () =>
      subscribeMeetingUpdates(() => {
        void load();
      }),
    [load],
  );
  useEffect(() => {
    if (open && focusEditor.current) {
      input.current?.focus();
      focusEditor.current = false;
    }
  }, [open]);
  const save = async () => {
    if (busy || !draft.content.trim() || draft.content.length > MAX_CHARACTERS) return;
    setBusy(true);
    setError('');
    try {
      const { note } = await meetingsApi.addMemberNote(meeting.id, draft);
      if (!active.current) return;
      setNotes((current) =>
        current.some((item) => item.id === note.id) ? current : [...current, note],
      );
      onDraft(emptyMeetingNoteDraft());
      setOpen(false);
      setSaved(true);
    } catch {
      if (active.current) setError(copy.saveFailed);
    } finally {
      if (active.current) setBusy(false);
    }
  };
  const remove = async (id: string) => {
    try {
      await meetingsApi.deleteMemberNote(meeting.id, id);
      if (active.current) {
        setNotes((current) => current.filter((note) => note.id !== id));
        await load();
      }
    } catch {
      if (active.current) setError(copy.removeFailed);
    }
  };
  const importFile = async (selected: File) => {
    setBusy(true);
    setError('');
    try {
      if (!/\.(txt|md)$/iu.test(selected.name) || selected.size > MAX_CHARACTERS * 4)
        throw new Error('unsupported');
      const content = await selected.text();
      if (!content.trim() || content.length > MAX_CHARACTERS || content.includes('\0'))
        throw new Error('unsupported');
      if (content.length + draft.content.length + (draft.content ? 2 : 0) > MAX_CHARACTERS)
        throw new Error('unsupported');
      if (!active.current) return;
      onDraft({
        content: draft.content ? `${draft.content}\n\n${content}` : content,
        clientNoteId: draft.clientNoteId,
        fileName: selected.name.slice(0, 255),
      });
      focusEditor.current = true;
      setOpen(true);
    } catch {
      if (active.current) setError(copy.fileFailed);
    } finally {
      if (active.current) setBusy(false);
    }
  };
  const add = () => {
    setSaved(false);
    focusEditor.current = true;
    setOpen(true);
  };
  const chat = () =>
    ws.send(copy.chatPrompt, { ...emptyTurnContext(), meetingIds: [meeting.id] }, false, {
      kind: 'meeting',
      id: meeting.id,
    });
  return (
    <section className="ws-member-notes" aria-label={copy.title}>
      <div className="ws-member-notes-heading">
        <span className="ws-member-notes-symbol" aria-hidden="true">
          <NotebookPenIcon size={19} strokeWidth={1.6} />
        </span>
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.description}</p>
        </div>
      </div>
      <p className="ws-member-notes-access">{copy.access}</p>
      {!!notes.length && (
        <div className="ws-member-notes-list">
          {notes.map((note) => (
            <Note key={note.id} note={note} onRemove={remove} />
          ))}
        </div>
      )}
      {nextOffset !== null && (
        <button className="ws-text-button" disabled={loading} onClick={() => void load(nextOffset)}>
          {copy.more}
        </button>
      )}
      {error && (
        <div className="ws-member-notes-error" role="alert">
          {error}
          {error === copy.loadFailed && (
            <button className="ws-text-button" onClick={() => void load()}>
              {copy.retry}
            </button>
          )}
        </div>
      )}
      {loading && !notes.length && (
        <p className="ws-member-notes-status" role="status">
          {copy.loading}
        </p>
      )}
      {saved && (
        <p className="ws-member-notes-status" role="status">
          <CheckIcon size={14} aria-hidden />
          {copy.saved}
        </p>
      )}
      {canAdd &&
        (open ? (
          <form
            className="ws-member-note-editor"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <label htmlFor={`member-note-${meeting.id}`}>{copy.label}</label>
            <textarea
              id={`member-note-${meeting.id}`}
              ref={input}
              rows={5}
              value={draft.content}
              maxLength={MAX_CHARACTERS}
              placeholder={copy.placeholder}
              disabled={busy}
              onChange={(event) => {
                setSaved(false);
                onDraft({ ...draft, content: event.target.value });
              }}
              onKeyDown={(event) => {
                if (
                  (event.metaKey || event.ctrlKey) &&
                  event.key === 'Enter' &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  void save();
                }
              }}
            />
            <div className="ws-member-note-editor-meta">
              <span>{draft.fileName ?? copy.shortcut}</span>
              <span>
                {formatCopy(copy.count, { count: draft.content.length, max: MAX_CHARACTERS })}
              </span>
            </div>
            <div className="ws-member-note-editor-actions">
              <button
                className="ws-button primary"
                disabled={busy || !draft.content.trim() || draft.content.length > MAX_CHARACTERS}
              >
                {busy ? copy.saving : copy.save}
              </button>
              <button
                type="button"
                className="ws-text-button"
                disabled={busy}
                onClick={() => {
                  onDraft(emptyMeetingNoteDraft());
                  setOpen(false);
                }}
              >
                {copy.cancel}
              </button>
            </div>
          </form>
        ) : (
          <div className="ws-member-notes-actions">
            <button className="ws-button" onClick={add}>
              <PlusIcon size={15} aria-hidden />
              {copy.add}
            </button>
            <button
              className="ws-text-button"
              disabled={busy}
              onClick={() => file.current?.click()}
            >
              <UploadIcon size={15} aria-hidden />
              {copy.upload}
            </button>
            <button className="ws-text-button" onClick={chat}>
              <MessageSquareIcon size={15} aria-hidden />
              {copy.chat}
            </button>
          </div>
        ))}
      <input
        type="file"
        ref={file}
        hidden
        accept=".txt,.md,text/plain,text/markdown"
        onChange={(event) => {
          const selected = event.target.files?.[0];
          event.target.value = '';
          if (selected) void importFile(selected);
        }}
      />
    </section>
  );
}
